import { useState, useRef, useCallback } from 'react';
import { Upload, Loader2, CheckCircle, AlertCircle, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { uploadDerushFile, advanceVEStatus } from '@/services/data/veActions';
import { toast } from 'sonner';

interface VEUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoContentId: string;
  shootingId: string;
  videoTitle: string | null;
  onComplete?: () => void;
}

interface FileEntry {
  id: string;
  file: File;
  progress: number;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
}

const generateFileId = (): string => `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

export function VEUploadDialog({
  open,
  onOpenChange,
  videoContentId,
  shootingId,
  videoTitle,
  onComplete,
}: Readonly<VEUploadDialogProps>) {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFilesSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    const newEntries: FileEntry[] = Array.from(selected).map(file => ({
      id: generateFileId(),
      file,
      progress: 0,
      status: 'idle',
    }));
    setFiles(prev => [...prev, ...newEntries]);
    // Reset input
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const updateFileProgress = useCallback((fileId: string, progress: number) => {
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress } : f));
  }, []);

  const updateFileStatus = useCallback((
    fileId: string,
    status: FileEntry['status'],
    progress?: number,
    error?: string
  ) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId
        ? { ...f, status, progress: progress ?? f.progress, error }
        : f
    ));
  }, []);

  const handleUpload = useCallback(async () => {
    const pending = files.filter(f => f.status !== 'success');
    if (pending.length === 0) return;

    setIsUploading(true);
    let successCount = 0;

    for (const entry of files) {
      if (entry.status === 'success') continue;

      const fileId = entry.id;
      updateFileStatus(fileId, 'uploading', 0);

      const result = await uploadDerushFile(
        shootingId,
        videoContentId,
        entry.file,
        (progress) => updateFileProgress(fileId, progress)
      );

      updateFileStatus(
        fileId,
        result.success ? 'success' : 'error',
        result.success ? 100 : undefined,
        result.error
      );

      if (result.success) successCount++;
    }

    setIsUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} fichier${successCount > 1 ? 's' : ''} uploadé${successCount > 1 ? 's' : ''} dans Derush`);
      onComplete?.();
    }
  }, [files, shootingId, videoContentId, onComplete, updateFileProgress, updateFileStatus]);

  const handleValidateToMontage = useCallback(async () => {
    setIsUploading(true);
    const result = await advanceVEStatus(videoContentId);
    setIsUploading(false);

    if (result.success) {
      toast.success('Vidéo envoyée au monteur');
      onOpenChange(false);
      onComplete?.();
    } else {
      toast.error(result.error || 'Erreur de validation');
    }
  }, [videoContentId, onOpenChange, onComplete]);

  const allUploaded = files.length > 0 && files.every(f => f.status === 'success');
  const hasPending = files.some(f => f.file && f.status !== 'success');

  return (
    <Dialog open={open} onOpenChange={(v) => !isUploading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload sources — Derush</DialogTitle>
          <DialogDescription>
            {videoTitle || 'Vidéo sans titre'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* File list */}
          {files.map((entry) => (
            <div key={entry.id} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {entry.status === 'uploading' && <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />}
                  {entry.status === 'success' && <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />}
                  {entry.status === 'error' && <AlertCircle className="h-3 w-3 text-destructive flex-shrink-0" />}
                  <span className="text-sm text-foreground/70 truncate">{entry.file.name}</span>
                </div>
                {entry.status === 'idle' && !isUploading && (
                  <button onClick={() => removeFile(entry.id)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {(entry.status === 'uploading' || entry.status === 'success') && (
                <Progress value={entry.progress} className="h-1" />
              )}
              {entry.error && <p className="text-xs text-destructive">{entry.error}</p>}
            </div>
          ))}

          {/* Add files button */}
          <input
            ref={inputRef}
            type="file"
            accept="video/*,audio/*,.zip,.rar"
            multiple
            className="hidden"
            onChange={handleFilesSelected}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter des fichiers
          </Button>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          {/* Upload button */}
          {hasPending && (
            <Button
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Upload en cours…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Uploader {files.filter(f => f.status !== 'success').length} fichier{files.filter(f => f.status !== 'success').length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}

          {/* Validate to montage button - visible once files are uploaded */}
          {allUploaded && (
            <Button
              onClick={handleValidateToMontage}
              disabled={isUploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Valider → Envoyer au monteur
            </Button>
          )}

          {/* Cancel */}
          {!isUploading && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {allUploaded ? 'Fermer' : 'Annuler'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
